// ----------------------------------------------------------------------------
// Team availability: members × the team's service dates for a month.
// Click a cell to cycle Available → Unavailable → Tentative (saved for the
// member, who is notified and can change it back). "Set leave" takes someone
// off every roster until an admin ends it.
//
// Data: GET /api/organizations/churches/{churchId}/availability?groupId&year&month
// Columns are the team's services that month; if those can't be loaded the
// grid falls back to every day of the month.
// ----------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '@app/auth/AuthContext';
import { useGroup } from '@admin/shared/contexts/GroupContext';
import { availabilityApi, plansApi } from '@app/api/organization';
import type { AvailabilityStatusValue, MemberAvailabilityDto } from '@app/types';
import { Toast } from '@app/components/Notifications';
import { Avatar, Button, ConfirmDialog, EmptyState, ErrorState, PageHeader, cx } from '@app/components/ui';

const CELL: Record<AvailabilityStatusValue, { glyph: string; cls: string; label: string }> = {
  Available: { glyph: '✓', cls: 'bg-ok-bg text-ok', label: 'Available' },
  Blocked: { glyph: '✕', cls: 'bg-acc-bg text-acc', label: 'Unavailable' },
  Tentative: { glyph: '?', cls: 'bg-amb-bg text-amb', label: 'Tentative' },
};
const NEXT: Record<AvailabilityStatusValue, AvailabilityStatusValue> = { Available: 'Blocked', Blocked: 'Tentative', Tentative: 'Available' };

const statusOn = (row: MemberAvailabilityDto, day: number): AvailabilityStatusValue => row.days.find((d) => d.day === day)?.status ?? 'Available';

export const TeamAvailability: React.FC = () => {
  const { user } = useAuth();
  const { groups, selectedGroupId, selectedGroup } = useGroup();
  const churchId = user?.churchId;
  const groupId = selectedGroupId ?? groups[0]?.id;

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
  const [rows, setRows] = useState<MemberAvailabilityDto[]>([]);
  const [serviceDays, setServiceDays] = useState<number[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [leaveTarget, setLeaveTarget] = useState<MemberAvailabilityDto | null>(null);
  const [toast, setToast] = useState<{ m: string; t: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    if (!churchId || !groupId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      setRows(await availabilityApi.getTeam(churchId, groupId, year, month));
    } catch {
      setError(true);
      setRows([]);
    } finally {
      setLoading(false);
    }
    // Service dates are a nicety on top of the grid; any failure falls back to every day.
    try {
      const plans = await plansApi.getRecent(churchId, 100, groupId);
      const days = Array.from(
        new Set(
          plans
            .map((p) => new Date(p.date))
            .filter((d) => d.getFullYear() === year && d.getMonth() + 1 === month)
            .map((d) => d.getDate()),
        ),
      ).sort((a, b) => a - b);
      setServiceDays(days.length ? days : null);
    } catch {
      setServiceDays(null);
    }
  }, [churchId, groupId, year, month]);

  useEffect(() => {
    void load();
  }, [load]);

  const days = useMemo(() => serviceDays ?? Array.from({ length: new Date(year, month, 0).getDate() }, (_, i) => i + 1), [serviceDays, year, month]);
  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const shift = (n: number) => {
    const d = new Date(year, month - 1 + n, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };

  const cycle = async (row: MemberAvailabilityDto, day: number) => {
    if (!churchId || !groupId) return;
    const next = NEXT[statusOn(row, day)];
    const apply = (r: MemberAvailabilityDto, s: AvailabilityStatusValue): MemberAvailabilityDto => ({
      ...r,
      updatedByAdmin: true,
      days: s === 'Available' ? r.days.filter((d) => d.day !== day) : [...r.days.filter((d) => d.day !== day), { day, status: s }],
    });
    const before = row;
    setRows((prev) => prev.map((r) => (r.userId === row.userId ? apply(r, next) : r)));
    try {
      await availabilityApi.setMemberDay(churchId, groupId, row.userId, year, month, day, next);
    } catch {
      setRows((prev) => prev.map((r) => (r.userId === row.userId ? before : r)));
      setToast({ m: `Couldn't update ${row.userName}'s availability`, t: 'error' });
    }
  };

  const setLeave = async (row: MemberAvailabilityDto, onLeave: boolean) => {
    if (!churchId || !groupId) return;
    try {
      await availabilityApi.setMemberLeave(churchId, groupId, row.userId, onLeave);
      setToast({ m: onLeave ? `${row.userName} is on leave` : `${row.userName} is back on the schedule`, t: 'success' });
      await load();
    } catch {
      setToast({ m: 'Could not update leave. Try again.', t: 'error' });
    }
  };

  const freeOn = (day: number) => rows.filter((r) => !r.onLeave && statusOn(r, day) === 'Available').length;

  return (
    <div>
      {toast && <Toast message={toast.m} type={toast.t} onClose={() => setToast(null)} />}
      <PageHeader
        eyebrow={`${selectedGroup?.name ?? 'Team'} · ${monthLabel}`}
        title="Team availability"
        actions={
          <div className="flex flex-wrap items-center gap-3.5 text-[12.5px] text-t3">
            {(['Available', 'Blocked', 'Tentative'] as const).map((s) => (
              <span key={s} className="flex items-center gap-1.5">
                <span className={cx('w-[18px] h-[18px] rounded-[5px] text-[11px] font-bold flex items-center justify-center', CELL[s].cls)} aria-hidden="true">
                  {CELL[s].glyph}
                </span>
                {CELL[s].label}
              </span>
            ))}
            <span className="flex items-center gap-1.5">
              <span className="w-[18px] h-[18px] rounded-[5px] bg-amb-bg border border-amb" aria-hidden="true" />
              On leave
            </span>
          </div>
        }
      />

      <div className="flex items-center gap-2 mb-3.5">
        <button type="button" onClick={() => shift(-1)} aria-label="Previous month" className="w-8 h-8 rounded-full bg-fill hover:bg-fill2 text-t2 flex items-center justify-center">
          <ChevronLeft size={16} aria-hidden="true" />
        </button>
        <span className="text-[15px] font-semibold min-w-[150px] text-center" aria-live="polite">
          {monthLabel}
        </span>
        <button type="button" onClick={() => shift(1)} aria-label="Next month" className="w-8 h-8 rounded-full bg-fill hover:bg-fill2 text-t2 flex items-center justify-center">
          <ChevronRight size={16} aria-hidden="true" />
        </button>
        <span className="text-[12.5px] text-t4 ml-2">{serviceDays ? 'Showing service dates' : 'Showing every day'}</span>
      </div>

      {error ? (
        <ErrorState title="Couldn't load availability" body="Check your connection and try again." onRetry={load} retrying={loading} />
      ) : loading ? (
        <span className="wp-skeleton block h-80 rounded-[24px]" aria-busy="true" />
      ) : rows.length === 0 ? (
        <EmptyState title="No members yet" body={`Add people to ${selectedGroup?.name ?? 'this team'} to see their availability here.`} />
      ) : (
        <div className="rounded-[24px] border border-line bg-card overflow-auto">
          <div role="grid" aria-label={`Availability for ${monthLabel}`} className="min-w-[760px]">
            <div role="row" className="flex gap-1.5 px-[18px] py-3.5 border-b border-line items-end">
              <span role="columnheader" className="w-[220px] shrink-0 wp-eyebrow">
                Member
              </span>
              <span className="w-[104px] shrink-0" aria-hidden="true" />
              {days.map((d) => {
                const date = new Date(year, month - 1, d);
                return (
                  <div key={d} role="columnheader" className="flex-1 min-w-[34px] text-center">
                    <div className="text-[11px] font-semibold text-t4 uppercase">{date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                    <div className="font-mono text-[16px] font-bold">{d}</div>
                    {serviceDays && <div className="text-[11px] text-t4">{freeOn(d)} free</div>}
                  </div>
                );
              })}
            </div>
            {rows.map((row) => (
              <div key={row.userId} role="row" className="flex gap-1.5 px-[18px] py-2 border-b border-line last:border-0 items-center">
                <div role="rowheader" className="w-[220px] shrink-0 flex items-center gap-2.5 min-w-0">
                  <Avatar name={row.userName} size={30} />
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-medium truncate">{row.userName}</div>
                    <div className="text-[11.5px] text-t4 truncate">{row.role}</div>
                  </div>
                </div>
                <div className="w-[104px] shrink-0">
                  {row.onLeave ? (
                    <Button size="sm" variant="dark" className="!h-[30px] !text-[12px]" onClick={() => setLeave(row, false)}>
                      End leave
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" className="!h-[30px] !text-[12px]" onClick={() => setLeaveTarget(row)}>
                      Set leave
                    </Button>
                  )}
                </div>
                {row.onLeave ? (
                  <div className="flex-1 min-w-0 h-[34px] rounded-[9px] bg-amb-bg text-amb text-[12.5px] font-semibold flex items-center justify-center gap-1.5 px-2 text-center">
                    On leave{row.leaveReason ? ` (${row.leaveReason})` : ''} · not scheduled until leave ends
                  </div>
                ) : (
                  days.map((d) => {
                    const s = statusOn(row, d);
                    return (
                      <button
                        key={d}
                        type="button"
                        role="gridcell"
                        onClick={() => cycle(row, d)}
                        aria-label={`${row.userName}, ${new Date(year, month - 1, d).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}: ${CELL[s].label}. Change`}
                        className={cx('flex-1 min-w-[34px] h-[34px] rounded-[9px] text-[12px] font-bold', CELL[s].cls)}
                        style={{ transition: 'background .15s ease' }}
                      >
                        {CELL[s].glyph}
                      </button>
                    );
                  })
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="text-[12.5px] text-t4 mt-3">
        Click a cell to change it. Set leave takes someone off every upcoming roster until you end it. Members are notified when an admin updates their availability.
      </p>

      <ConfirmDialog
        isOpen={!!leaveTarget}
        onClose={() => setLeaveTarget(null)}
        onConfirm={() => (leaveTarget ? setLeave(leaveTarget, true) : undefined)}
        title={leaveTarget ? `Put ${leaveTarget.userName} on leave?` : 'Put on leave?'}
        message="They stay on the team but won't be scheduled until you end their leave. Roster pickers show them as on leave."
        confirmText="Put on leave"
      />
    </div>
  );
};
